/* eslint-disable react-refresh/only-export-components */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { featureFlagsApi, type FeatureFlag } from '../api';
import { useAuth } from './AuthContext';

interface FeatureFlagsContextType {
  flags: FeatureFlag[];
  loading: boolean;
  isEnabled: (key: string) => boolean;
  setFlag: (key: string, enabled: boolean) => Promise<void>;
  reload: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

// Holds the current user's resolved feature flags. Fetched once the user is
// authenticated and cleared on logout. Flags are per-user, so this always
// reflects the signed-in user — never a global state.
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  // The auth state the current `flags` were fetched under (null = never
  // fetched). `loading` is DERIVED from it so that the instant auth flips,
  // consumers see loading=true in the very same render pass — an effect-set
  // boolean would lag one render behind and let a flag-gated route decide on
  // stale/empty flags during a hard-reload deep-link.
  const [fetchedFor, setFetchedFor] = useState<boolean | null>(null);
  const loading = fetchedFor !== isAuthenticated;

  // Gated on auth so a logged-out user clears to defaults and we never hit the
  // API anonymously. Recreated when auth flips, which re-runs the effect below.
  const reload = useCallback(async () => {
    if (!isAuthenticated) {
      setFlags([]);
      return;
    }
    try {
      setFlags(await featureFlagsApi.list());
    } catch {
      setFlags([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload().finally(() => {
      if (!cancelled) setFetchedFor(isAuthenticated);
    });
    return () => {
      cancelled = true;
    };
  }, [reload, isAuthenticated]);

  const setFlag = useCallback(async (key: string, enabled: boolean) => {
    setFlags(await featureFlagsApi.setFlag(key, enabled));
  }, []);

  const isEnabled = useCallback(
    (key: string) => flags.find((f) => f.key === key)?.enabled ?? false,
    [flags],
  );

  const value = useMemo<FeatureFlagsContextType>(
    () => ({ flags, loading, isEnabled, setFlag, reload }),
    [flags, loading, isEnabled, setFlag, reload],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}

// Convenience hook for gating a single feature.
export function useFeatureFlag(key: string): boolean {
  return useFeatureFlags().isEnabled(key);
}
